import { FC, useEffect } from 'react';
import { useQuery } from 'packages/lib/utils/hooks/use-query';
import { personaAtom } from 'packages/at-shell/state/recoil/ui';
import isNumber from 'predicates/number';
import z from 'zod';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { ZodEffects, ZodEnum } from 'zod';
import { taskAtom } from 'packages/at-shell/state/recoil/task';
import useSWR from 'swr';
import { userEmail } from 'packages/lib/auth';

// import { zodCaseSensitiveURLEnumPreprocess } from 'packages/at-shell/utils/multitask-workflow-query-params';

// Enums will be thrown error for incorrect case in URL
// But once we go to an incorrect URL in query params and show error, if we correct the URL, chrome will keep going to the cached previous URL and we will not get correct case
// Check the https://jira.ci.motional.com/atljira/browse/ANNOT-1938 for an example issue
// This preprocess will check if the enum has a valid option that is just a case-difference, in such case use a valid enum value
// If not, just proceed with validation and fail
export const zodCaseSensitiveURLEnumPreprocess = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  zodEnum: ZodEnum<any>,
  defaultValue: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ZodEffects<any> => {
  return z.preprocess(
    (val) => {
      if (!val) {
        return defaultValue;
      }

      return (
        Object.values(zodEnum.Values).find(
          (stateVal) =>
            stateVal.toLowerCase().trim() ===
            (val as string).toLowerCase().trim()
        ) ?? val
      );
    },
    defaultValue ? zodEnum.default(defaultValue) : zodEnum
  );
};

export const zAnnotationToolUrlQueryState = z.object({
  sample: z.string().optional(),
  frame: z
    .string()
    .optional()
    .transform((prev) => {
      if (prev === undefined) return prev;
      const n = parseInt(prev);
      if (isNaN(n) || !isNumber(n)) {
        return undefined;
      }
      return n;
    }),
  sensor: z.string().optional(),
  segment: z.string().optional(),
  object: z.string().optional(),
  linking_mask: z.string().optional(),
  event_token: z.string().optional(),
  dev_mode: z
    .string()
    .optional()
    .transform((prev) => !!prev),
});

export type AnnotationToolQueryParams = z.infer<
  typeof zAnnotationToolUrlQueryState
>;

export const useToolQueryparams = (): AnnotationToolQueryParams => {
  const query = useQuery();
  return zAnnotationToolUrlQueryState.parse(Object.fromEntries(query));
};

export const SyncAppPersonaAtom: FC<React.PropsWithChildren<unknown>> = () => {
  const { data: user } = useSWR('user-email', userEmail);
  const task = useRecoilValue(taskAtom);
  const setPersona = useSetRecoilState(personaAtom);
  useEffect(() => {
    if (!user) return;
    if (!task) return;
    const role = ['qa', 'audit'].find((x) =>
      task.annotationState.toLowerCase().includes(x)
    )
      ? 'qa'
      : 'label';
    if (!role || user !== task.assignee) {
      setPersona('view');
      return;
    }
    setPersona(role);
  }, [setPersona, task, user]);
  return null;
};
